import type { WithID } from '@ps/models/lib/base';
import type { Profile } from '@ps/models/lib/profile';

import {
  type AnyVideo,
  type BareVideoInfo,
  type OrderReviewVideoInfo,
  type OrderThankYouVideoInfo,
  type PopClipVideoInfo,
  type VideoInfo,
  VideoType,
} from './types';

export function isPopClipVideo(video: AnyVideo): video is PopClipVideoInfo {
  return video.type === VideoType.Clips;
}

export function isBareVideo(video: AnyVideo): video is BareVideoInfo {
  return video.type === VideoType.Bare;
}

export function isOrderReviewVideo(
  video: AnyVideo,
): video is OrderReviewVideoInfo {
  return video.type === VideoType.Review;
}

export function isOrderThankYouVideo(
  video: AnyVideo,
): video is OrderThankYouVideoInfo {
  return video.type === VideoType.ThankYou;
}

export function isOwnerOfVideo(
  video: AnyVideo,
  userId: string | undefined,
): boolean {
  return video.author?.id === userId;
}

export function videoInfo(video: AnyVideo): VideoInfo {
  return {
    get videoId() {
      switch (video.type) {
        case VideoType.Clips:
          return video.clipId;
        case VideoType.Review:
        case VideoType.ThankYou:
          return video.messageId;
        case VideoType.Bare:
          return video.id;
      }
    },

    get sellerId() {
      switch (video.type) {
        case VideoType.Clips:
          return video.author?.id;
        case VideoType.Review:
          return video.to.id;
        case VideoType.ThankYou:
          return video.sellerId;
        case VideoType.Bare:
          return video.sellerId;
      }
    },

    get relatedProfile(): WithID<Profile> | undefined {
      switch (video.type) {
        case VideoType.Review:
        case VideoType.ThankYou:
          return video.to;
        default:
          return video.author;
      }
    },

    get primaryProfile(): WithID<Profile> | undefined {
      switch (video.type) {
        case VideoType.Review:
          return video.to;
        case VideoType.ThankYou:
          return video.from;
        default:
          return video.author;
      }
    },

    get listings() {
      return video.listings?.length ? video.listings : [];
    },

    get defaultListing() {
      return video.listings?.[0];
    },

    get defaultListingImage() {
      const listing = video.listings?.[0];
      return listing?.defaultImage ?? listing?.extraImages?.[0];
    },
  };
}

export function formatCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toString();
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function getVideoDate(video: AnyVideo): Date | undefined {
  const raw = video.createdAt;
  if (!raw) return undefined;
  if (raw instanceof Date) return raw;
  if (typeof raw === 'string' || typeof raw === 'number') {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? undefined : d;
  }
  // Firestore Timestamp with toDate()
  if (
    typeof raw === 'object' &&
    'toDate' in raw &&
    typeof raw.toDate === 'function'
  ) {
    return raw.toDate();
  }
  // Serialized Firestore Timestamp shape: `{ seconds, nanoseconds }`
  // (the form `JSON.stringify(timestamp)` produces — also what
  // `toJSON()`'d server-action responses carry).
  if (typeof raw === 'object' && 'seconds' in raw) {
    const d = new Date((raw as { seconds: number }).seconds * 1000);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}
